<template>
  <div class="dashboard">
    <div class="container">
      <div class="tab-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'search' }"
          @click="activeTab = 'search'"
        >Kit Search &amp; Upload</button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'queue' }"
          @click="switchToQueue"
        >Lab Work Queue</button>
      </div>

      <!-- ── Lab Work Queue ── -->
      <div class="work-queue" v-show="activeTab === 'queue'">
        <!-- Sticky filter / search bar -->
        <div class="queue-filters">
          <div class="filter-row">
            <div class="filter-field filter-search">
              <label class="filter-label">Search</label>
              <input
                type="text"
                class="filter-input"
                v-model="searchInput"
                placeholder="Kit ID, customer name, email, or sample ID"
              />
            </div>
            <div class="filter-field">
              <label class="filter-label">Status</label>
              <select class="filter-input" v-model="statusFilter">
                <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="filter-field">
              <label class="filter-label">Sample Type</label>
              <select class="filter-input" v-model="sampleTypeFilter">
                <option value="all">All</option>
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
              </select>
            </div>
            <div class="filter-field">
              <label class="filter-label">From Date</label>
              <input type="date" class="filter-input" v-model="dateFrom" />
            </div>
            <div class="filter-field">
              <label class="filter-label">To Date</label>
              <input type="date" class="filter-input" v-model="dateTo" />
            </div>
            <div class="filter-field filter-actions">
              <button
                class="clear-filters-btn"
                @click="clearFilters"
                :disabled="!hasActiveFilters"
              >
                Clear Filters
              </button>
            </div>
          </div>
          <div class="filter-summary">
            <span class="results-count">{{ filteredCount }} result{{ filteredCount === 1 ? '' : 's' }}</span>
            <span class="filter-hint" v-if="hasActiveFilters">filtered from {{ totalCount }}</span>
          </div>
        </div>

        <div class="queue-columns" v-if="filteredCount > 0">
          <div
            class="queue-col"
            v-for="col in queueColumns"
            :key="col.key"
          >
            <div class="queue-col-header" :style="{ borderTopColor: col.color }">
              <span class="queue-col-title">{{ col.title }}</span>
              <span class="queue-col-count">{{ (filteredBuckets[col.key] || []).length }}</span>
            </div>
            <div class="queue-col-body">
              <div
                class="queue-card"
                :class="{ highlighted: qk.kit_id === highlightedKitId }"
                v-for="qk in (filteredBuckets[col.key] || [])"
                :key="qk.id"
                :ref="'card-' + qk.kit_id"
              >
                <div class="queue-card-id">{{ convertKitId(qk.kit_id) }}</div>
                <div class="queue-card-status" :style="{ color: col.color }">{{ qk.status_label }}</div>
                <div class="queue-card-meta" v-if="qk.user">{{ qk.user.first_name }} {{ qk.user.last_name }}</div>
                <div class="queue-card-due" v-if="qk.expected_report_at">
                  Due {{ formatDate(qk.expected_report_at) }}
                </div>
                <!-- Uploaded FASTQ file info (Sequencing / Completed) -->
                <div class="queue-card-files" v-if="qk.mtc_lab_files && qk.mtc_lab_files.length">
                  <div class="queue-files-label">FASTQ files ({{ qk.mtc_lab_files.length }})</div>
                  <ul class="queue-files-list">
                    <li v-for="f in qk.mtc_lab_files" :key="f.id" :title="f.file_name">{{ f.file_name }}</li>
                  </ul>
                </div>
                <!-- Completed Kits: surface the business report status (set outside the lab). -->
                <div class="queue-card-report" v-if="qk._bucket === 'completed'">
                  <span v-if="qk.status_int === 5">Analysis Complete</span>
                  <span v-else-if="qk.status_int === 15">Report Ready</span>
                  <span v-else>FASTQ uploaded · report pending</span>
                </div>
                <template v-if="nextActionForKit(qk)">
                  <button
                    v-if="nextActionForKit(qk).type === 'advance'"
                    class="queue-advance-btn"
                    :disabled="advancingKitId === qk.kit_id"
                    @click="advanceStatus(qk, nextActionForKit(qk).action)"
                  >
                    {{ advancingKitId === qk.kit_id ? 'Saving...' : nextActionForKit(qk).label }}
                  </button>
                  <button
                    v-else-if="nextActionForKit(qk).type === 'upload'"
                    class="queue-advance-btn upload"
                    @click="openUploadForKit(qk)"
                  >
                    {{ nextActionForKit(qk).label }}
                  </button>
                </template>
              </div>
              <div class="queue-empty" v-if="(filteredBuckets[col.key] || []).length === 0">
                No kits
              </div>
            </div>
          </div>
        </div>
        <div class="queue-no-results" v-else>
          No kits match the selected filters.
        </div>
        <span class="queue-error" v-if="queueError">{{ queueError }}</span>
      </div>

      <div class="search-section" v-show="activeTab === 'search'">
        <h2 class="section-title">MTC Lab Dashboard</h2>
        <div class="search-wrap">
          <div class="input-wrap">
            <input
              v-mask="'XX - XX - XXX - XXXX'"
              id="mtc-kit-id"
              type="text"
              class="search-input"
              placeholder="Enter Kit ID"
              name="kit_id"
              v-model="kit_id"
              @paste.prevent
              @keyup.enter="searchKit"
              v-validate="{ required: true, length: 20 }"
            />
            <span class="validate-error" v-if="errors.has('kit_id')">
              {{ errors.first("kit_id") }}
            </span>
          </div>
          <button
            class="search-btn"
            @click.prevent="$validator.validateAll()"
            @click="searchKit"
            :disabled="!isComplete"
          >
            Search
          </button>
        </div>
        <span class="error-msg" v-if="errorMessage">{{ errorMessage }}</span>
      </div>

      <div v-if="kit && activeTab === 'search'" class="kit-section">
        <div class="kit-info">
          <h3>Kit Information</h3>
          <div class="kit-details">
            <div class="detail-row">
              <span class="label">Kit ID:</span>
              <span class="value">{{ kit.kit_id }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Kit Status:</span>
              <span class="value status-badge" :class="kitStatusClass">{{ getStatusLabel(kitStatusInt) }}</span>
            </div>
            <!-- Report SLA (shown once the kit is at/after Lab Received and not yet completed) -->
            <template v-if="showReportSla">
              <div class="detail-row">
                <span class="label">Report:</span>
                <span class="value status-badge processing">Report Pending</span>
              </div>
              <div class="detail-row" v-if="kit.expected_report_at">
                <span class="label">Expected Report:</span>
                <span class="value">{{ formatDate(kit.expected_report_at) }}</span>
              </div>
              <div class="detail-row" v-if="dueInBusinessDays !== null">
                <span class="label">Due In:</span>
                <span class="value">{{ dueInBusinessDays }} Business Day{{ dueInBusinessDays === 1 ? '' : 's' }}</span>
              </div>
            </template>
            <!-- Lab stage (internal) -->
            <div class="detail-row" v-if="kitLabStageLabel">
              <span class="label">Lab Stage:</span>
              <span class="value">{{ kitLabStageLabel }}</span>
            </div>
            <!-- Lab workflow controls for lab users -->
            <div class="detail-row" v-if="labNextAction">
              <span class="label">Action:</span>
              <button
                class="value queue-advance-btn inline"
                :disabled="advancingKitId === kit.kit_id"
                @click="advanceStatus(kit, labNextAction.action)"
              >
                {{ advancingKitId === kit.kit_id ? 'Saving...' : labNextAction.label }}
              </button>
            </div>
            <div class="detail-row">
              <span class="label">Report Status:</span>
              <span v-if="files.length === 0" class="value status-badge no-reports">No Reports</span>
              <span v-else-if="allFilesCompleted" class="value status-badge completed">Completed</span>
              <span v-else class="value status-badge processing">Processing</span>
            </div>
            <div class="detail-row" v-if="kit.collected_first_name">
              <span class="label">Name:</span>
              <span class="value">{{ kit.collected_first_name }} {{ kit.collected_last_name }}</span>
            </div>
            <div class="detail-row" v-if="kit.collected_email">
              <span class="label">Email:</span>
              <span class="value">{{ kit.collected_email }}</span>
            </div>
            <div class="detail-row" v-if="kit.sampling_date">
              <span class="label">Sampling Date:</span>
              <span class="value">{{ kit.sampling_date }}</span>
            </div>
          </div>
          <div class="kit-timeline-wrap">
            <KitTimelineV2 :kit="kit" :reason="kit.reason_on_hold" />
          </div>
        </div>

        <div class="status-warning" v-if="kitLabStage === 'new'">
          <p class="warning-msg">Accept the kit before it can be processed</p>
        </div>
        <div class="status-warning" v-else-if="kitLabStage === 'accepted'">
          <p class="warning-msg">Click "Next Step" to begin DNA Extraction before uploading FASTQ files</p>
        </div>
        <div class="status-warning" v-else-if="kitLabStage === 'dna_extraction'">
          <p class="warning-msg">Click "Next Step" to begin Sequencing before uploading FASTQ files</p>
        </div>

        <!-- Upload form only renders while the kit is "Sequencing In Progress". A
             successful upload moves the lab stage to "Completed Kit" and the
             business status to Lab Received, after which the read-only file listing
             is shown instead. -->
        <div class="upload-section" v-if="canUpload && files.length === 0">
          <h3>Upload FASTQ Files</h3>
          <div class="upload-area">
            <!-- Per-Sample FASTQ R1/R2 -->
            <div class="upload-group" v-if="indoorSamples.length > 0">
              <div class="upload-group-header">
                <span class="upload-group-title">Indoor Samples</span>
                <span class="upload-group-hint">(R1 + R2 per sample)</span>
              </div>
              <div
                class="sample-card"
                v-for="(sample, index) in indoorSamples"
                :key="'in-' + index"
              >
                <div class="sample-card-header">
                  {{ sampleDisplayName(sample, index, 'indoor') }}
                  <span class="sample-slug">({{ sampleSlug(sample, index, 'indoor') }})</span>
                </div>
                <div class="fastq-row-sample" v-for="read in ['R1', 'R2']" :key="'in-' + index + '-' + read">
                  <input
                    type="file"
                    :ref="'fastqInInput_' + index + '_' + read"
                    @change="onSampleFastqSelected($event, 'indoor', index, read)"
                    class="file-input"
                    :id="'fastq-in-' + index + '-' + read"
                    accept=".fastq,.fastq.gz"
                  />
                  <label
                    :for="'fastq-in-' + index + '-' + read"
                    class="file-label fastq-label"
                    :class="{ 'slot-missing': isSlotMissing('indoor', index, read) }"
                  >
                    <span v-if="!getStagedFastq('indoor', index, read)">Upload {{ read }} FASTQ <span class="required-marker">*</span></span>
                    <span v-else>{{ getStagedFastq('indoor', index, read).name }}</span>
                  </label>
                  <div v-if="getStagedFastq('indoor', index, read)" class="selected-files">
                    <div class="file-tag fastq-tag">
                      {{ getStagedFastq('indoor', index, read).name }}
                      <button class="remove-file" @click="removeSampleFastq('indoor', index, read)">&times;</button>
                    </div>
                  </div>
                  <div
                    class="slot-error"
                    v-if="getSlotTypeError('indoor', index, read) || isSlotMissing('indoor', index, read)"
                  >
                    {{ getSlotTypeError('indoor', index, read) || (read + ' FASTQ file is required') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="upload-group" v-if="outdoorSamples.length > 0">
              <div class="upload-group-header">
                <div>
                  <span class="upload-group-title">Outdoor Samples</span>
                  <span class="upload-group-hint">(Optional)</span>
                </div>
                <button
                  type="button"
                  class="skip-outdoor-btn"
                  :class="{ 'is-skipped': outdoorSkipped }"
                  @click="toggleSkipOutdoor"
                >
                  {{ outdoorSkipped ? 'Include Outdoor Sample' : 'Skip Outdoor Sample' }}
                </button>
              </div>
              <div v-if="outdoorSkipped" class="outdoor-skipped-banner">
                <span>Outdoor sample skipped. Click "Include Outdoor Sample" if you wish to upload files.</span>
              </div>
              <div
                v-else
                class="sample-card"
                v-for="(sample, index) in outdoorSamples"
                :key="'out-' + index"
              >
                <div class="sample-card-header">
                  {{ sampleDisplayName(sample, index, 'outdoor') }}
                  <span class="sample-slug">({{ sampleSlug(sample, index, 'outdoor') }})</span>
                </div>
                <div class="fastq-row-sample" v-for="read in ['R1', 'R2']" :key="'out-' + index + '-' + read">
                  <input
                    type="file"
                    :ref="'fastqOutInput_' + index + '_' + read"
                    @change="onSampleFastqSelected($event, 'outdoor', index, read)"
                    class="file-input"
                    :id="'fastq-out-' + index + '-' + read"
                    accept=".fastq,.fastq.gz"
                  />
                  <label
                    :for="'fastq-out-' + index + '-' + read"
                    class="file-label fastq-label"
                    :class="{ 'slot-missing': isSlotMissing('outdoor', index, read) }"
                  >
                    <span v-if="!getStagedFastq('outdoor', index, read)">Upload {{ read }} FASTQ</span>
                    <span v-else>{{ getStagedFastq('outdoor', index, read).name }}</span>
                  </label>
                  <div v-if="getStagedFastq('outdoor', index, read)" class="selected-files">
                    <div class="file-tag fastq-tag">
                      {{ getStagedFastq('outdoor', index, read).name }}
                      <button class="remove-file" @click="removeSampleFastq('outdoor', index, read)">&times;</button>
                    </div>
                  </div>
                  <div
                    class="slot-error"
                    v-if="getSlotTypeError('outdoor', index, read) || isSlotMissing('outdoor', index, read)"
                  >
                    {{ getSlotTypeError('outdoor', index, read) || (read + ' FASTQ file is required') }}
                  </div>
                </div>
              </div>
            </div>

            <button
              class="upload-btn"
              @click="uploadFiles"
              :disabled="uploading || !canSubmitUpload"
            >
              {{ uploading ? "Uploading..." : "Upload" }}
            </button>
            <div class="upload-progress" v-if="uploading">
              <div class="upload-progress-bar">
                <div class="upload-progress-fill" :style="{ width: uploadProgress + '%' }"></div>
              </div>
              <span class="upload-progress-text">{{ uploadProgress }}%</span>
              <span class="upload-progress-file" v-if="uploadStatusText">{{ uploadStatusText }}</span>
            </div>
            <span class="error-msg" v-if="uploadErrorMsg">{{ uploadErrorMsg }}</span>
            <span class="success-msg" v-if="uploadSuccess">{{ uploadSuccessMsg }}</span>
          </div>
        </div>

        <!-- Read-only file listing shown for Received / Completed / any post-upload state. -->
        <div class="files-section" v-if="files.length > 0 && kitStatusInt >= 3">
          <h3>Uploaded Files</h3>

          <div class="grouped-files" v-if="indoorSampleFileGroups.length > 0">
            <div class="group-title">Indoor Samples</div>
            <div
              class="sample-file-group"
              v-for="group in indoorSampleFileGroups"
              :key="'in-group-' + group.index"
            >
              <div class="sample-file-name">{{ group.name }}</div>
              <ul class="sample-file-list">
                <li v-for="f in group.files" :key="f.id" class="sample-file-row">
                  <span class="file-type-badge" :class="'type-fastq'">{{ readLabel(f) }}</span>
                  <span class="file-name-cell">{{ f.file_name }}</span>
                  <span class="file-uploader-cell" v-if="uploaderName(f)">by {{ uploaderName(f) }}</span>
                  <span class="file-date-cell">{{ formatDate(f.created_at) }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="grouped-files" v-if="outdoorSampleFileGroups.length > 0">
            <div class="group-title">Outdoor Samples</div>
            <div
              class="sample-file-group"
              v-for="group in outdoorSampleFileGroups"
              :key="'out-group-' + group.index"
            >
              <div class="sample-file-name">{{ group.name }}</div>
              <ul class="sample-file-list">
                <li v-for="f in group.files" :key="f.id" class="sample-file-row">
                  <span class="file-type-badge" :class="'type-fastq'">{{ readLabel(f) }}</span>
                  <span class="file-name-cell">{{ f.file_name }}</span>
                  <span class="file-uploader-cell" v-if="uploaderName(f)">by {{ uploaderName(f) }}</span>
                  <span class="file-date-cell">{{ formatDate(f.created_at) }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="grouped-files" v-if="otherFiles.length > 0">
            <div class="group-title">Other Files</div>
            <table class="files-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Type</th>
                  <th>Uploaded At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in otherFiles" :key="file.id">
                  <td>{{ file.file_name }}</td>
                  <td>
                    <span
                      class="file-type-badge"
                      :class="'type-' + (file.file_type || 'file')"
                    >{{ (file.file_type || 'FILE').toUpperCase() }}</span>
                  </td>
                  <td>{{ formatDate(file.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { parseAndConvertKitId } from "../../components/mixins/parseAndConvertKitId";
import KitTimelineV2 from "./KitTimelineV2";

export default {
  name: "MtcLabDashboard",
  mixins: [parseAndConvertKitId],
  components: { KitTimelineV2 },
  data() {
    return {
      kit_id: null,
      searchKey: "",
      kit: null,
      errorMessage: "",
      indoorFastqStaged: {},
      outdoorFastqStaged: {},
      uploading: false,
      uploadSuccess: false,
      uploadSuccessMsg: "",
      uploadProgress: 0,
      // Files are uploaded one request at a time, so show which one is in
      // flight alongside the overall percentage.
      uploadStatusText: "",
      uploadErrorMsg: "",
      files: [],
      indoorFastqTypeErrors: {},
      outdoorFastqTypeErrors: {},
      outdoorSkipped: false,
      activeTab: "search",
      advancingKitId: null,
      queueError: "",
      // ── Work-queue filters ──
      searchInput: "",
      searchTerm: "",
      searchDebounceTimer: null,
      statusFilter: "all",
      sampleTypeFilter: "all",
      dateFrom: "",
      dateTo: "",
      highlightedKitId: null,
      highlightTimer: null,
      statusOptions: [
        { label: "All", value: "all" },
        { label: "New Kits", value: "new" },
        { label: "Accepted Kits", value: "accepted" },
        { label: "DNA Extraction In Progress", value: "dna_extraction" },
        { label: "Sequencing In Progress", value: "sequencing" },
        { label: "Completed Kits", value: "completed" }
      ],
      queueColumns: [
        { key: "new", title: "New Kits", color: "#AC75BD" },
        { key: "accepted", title: "Accepted Kits", color: "#00BBDC" },
        { key: "dna_extraction", title: "DNA Extraction In Progress", color: "#17A2B8" },
        { key: "sequencing", title: "Sequencing In Progress", color: "#6F42C1" },
        { key: "completed", title: "Completed Kits", color: "#43B02A" }
      ]
    };
  },
  computed: {
    // NOTE: do NOT map the `getMtcLabFiles` getter here — it collides by name
    // with the `getMtcLabFiles` action mapped in methods (Vue can't share a name
    // between a computed and a method → "assigned to but has no setter" warning).
    // The component only calls the action (await this.getMtcLabFiles(kitId)); the
    // getter's value is never read, so it's intentionally left unmapped.
    ...mapGetters(["getWorkQueue"]),
    workQueue() {
      return this.getWorkQueue || { buckets: {}, counts: {} };
    },
    // Flattened, pre-indexed kit list. Depends only on the work queue (not the
    // filter inputs), so Vue caches it and rebuilds the search index only when
    // the queue data itself changes — keeping per-keystroke filtering cheap.
    indexedKits() {
      const buckets = (this.workQueue && this.workQueue.buckets) || {};
      const out = [];
      Object.keys(buckets).forEach(bucketKey => {
        (buckets[bucketKey] || []).forEach(kit => {
          out.push(this.indexKit(kit, bucketKey));
        });
      });
      return out;
    },
    totalCount() {
      return this.indexedKits.length;
    },
    // Applies search + stage + date-range + sample-type filters and regroups the
    // surviving kits back into the lab-workflow buckets. The "stage" filter maps
    // directly to a bucket key (new / accepted / dna_extraction / sequencing / completed).
    filteredBuckets() {
      const term = this.searchTerm.trim().toLowerCase();
      const bucketFilter = this.statusFilter; // 'all' or a bucket key
      const from = this.dateFrom ? new Date(this.dateFrom + "T00:00:00") : null;
      const to = this.dateTo ? new Date(this.dateTo + "T23:59:59") : null;
      const sampleType = this.sampleTypeFilter;

      const result = { new: [], accepted: [], dna_extraction: [], sequencing: [], completed: [] };
      this.indexedKits.forEach(k => {
        if (term && k._haystack.indexOf(term) === -1) return;
        if (bucketFilter !== "all" && k._bucket !== bucketFilter) return;
        if (from && (!k._date || k._date < from)) return;
        if (to && (!k._date || k._date > to)) return;
        if (sampleType === "indoor" && !k._hasIndoor) return;
        if (sampleType === "outdoor" && !k._hasOutdoor) return;
        if (result[k._bucket]) result[k._bucket].push(k);
      });
      return result;
    },
    filteredCount() {
      const b = this.filteredBuckets;
      return Object.keys(b).reduce((sum, key) => sum + b[key].length, 0);
    },
    hasActiveFilters() {
      return (
        this.searchInput.trim() !== "" ||
        this.statusFilter !== "all" ||
        this.sampleTypeFilter !== "all" ||
        this.dateFrom !== "" ||
        this.dateTo !== ""
      );
    },
    // Report SLA panel shows once the business status is Lab Received (3), which
    // is set when the kit reaches the Completed stage (FASTQ upload) — the point
    // the expected report date is stamped and the report is pending.
    showReportSla() {
      return this.kitStatusInt === 3;
    },
    // The searched kit's lab-portal stage, mirroring the backend bucketOf() so
    // legacy kits (null lab_stage) still resolve from their business status.
    kitLabStage() {
      if (!this.kit) return null;
      const ls = this.kit.lab_stage;
      if (ls === "accepted" || ls === "dna_extraction" || ls === "sequencing" || ls === "completed") return ls;
      // Legacy 'processing' lab_stage maps to DNA Extraction.
      if (ls === "processing") return "dna_extraction";
      const s = this.kitStatusInt;
      if (s === 2) return "new";
      if (s === 16) return "accepted";
      if (s === 13) return "dna_extraction";
      if (s === 14) return "sequencing";
      if (s === 3 || s === 15 || s === 5) return "completed";
      return null;
    },
    kitLabStageLabel() {
      const labels = {
        new: "New Kit",
        accepted: "Accepted Kit",
        dna_extraction: "DNA Extraction In Progress",
        sequencing: "Sequencing In Progress",
        completed: "Completed Kit"
      };
      return labels[this.kitLabStage] || "";
    },
    // Whole business days remaining until expected_report_at (weekends excluded;
    // a server-side holiday-aware date was already stored on the kit).
    dueInBusinessDays() {
      if (!this.kit || !this.kit.expected_report_at) return null;
      const target = new Date(this.kit.expected_report_at);
      if (isNaN(target.getTime())) return null;
      const today = new Date();
      target.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (target <= today) return 0;
      let count = 0;
      const cursor = new Date(today);
      while (cursor < target) {
        cursor.setDate(cursor.getDate() + 1);
        const dow = cursor.getDay();
        if (dow !== 0 && dow !== 6) count += 1;
      }
      return count;
    },
    // Next lab action for the currently-searched kit, resolved from its lab stage.
    // 'sequencing' shows no button (the upload form drives completion); 'completed'
    // is terminal for the lab (report ready/completed are set outside the lab).
    labNextAction() {
      switch (this.kitLabStage) {
        case "new": return { action: "accept", label: "Accept" };
        case "accepted": return { action: "start_dna_extraction", label: "Next Step" };
        case "dna_extraction": return { action: "start_sequencing", label: "Next Step" };
        default: return null;
      }
    },
    isComplete() {
      return !!this.kit_id && this.kit_id.replace(/[-\s]/g, "").length === 11;
    },
    hasFilesToUpload() {
      const hasAny = bucket =>
        Object.keys(bucket).some(idx => {
          const slots = bucket[idx] || {};
          return !!(slots.R1 || slots.R2);
        });
      return hasAny(this.indoorFastqStaged) || hasAny(this.outdoorFastqStaged);
    },
    missingFastqSlots() {
      const missing = [];
      const check = (kind, samples) => {
        for (let i = 0; i < samples.length; i += 1) {
          ["R1", "R2"].forEach(read => {
            if (this.isSlotMissing(kind, i, read)) {
              missing.push({ kind, index: i, read });
            }
          });
        }
      };
      check("indoor", this.indoorSamples);
      check("outdoor", this.outdoorSamples);
      return missing;
    },
    canSubmitUpload() {
      if (this.indoorSamples.length === 0 && this.outdoorSamples.length === 0) {
        return false;
      }
      return this.missingFastqSlots.length === 0;
    },
    indoorSamples() {
      const arr = (this.kit && Array.isArray(this.kit.samples)) ? this.kit.samples : [];
      return arr.slice().sort((a, b) => (a.sample_order || 0) - (b.sample_order || 0));
    },
    outdoorSamples() {
      if (this.kit && this.kit.outdoor_sample) {
        return [{ value: this.kit.outdoor_sample }];
      }
      return [];
    },
    indoorSampleFileGroups() {
      return this.buildSampleGroups("indoor", this.indoorSamples);
    },
    outdoorSampleFileGroups() {
      return this.buildSampleGroups("outdoor", this.outdoorSamples);
    },
    otherFiles() {
      const PER_SAMPLE = new Set([
        "fastq_indoor_r1",
        "fastq_indoor_r2",
        "fastq_outdoor_r1",
        "fastq_outdoor_r2"
      ]);
      return this.files.filter(f => !PER_SAMPLE.has(f.file_type));
    },
    kitStatusInt() {
      if (!this.kit) return -1;
      const s = this.kit.status;
      if (typeof s === 'number') return s;
      const map = {
        'shipped': 0, 'activated': 1, 'lab_awaiting': 2, 'lab_received': 3,
        'on_hold': 4, 'analysis_completed': 5, 'problem': 6, 'delivered': 7,
        'archive': 8, 'returned': 9, 'analysis_failed': 10, 'partially_failed': 11, 'in_active': 12,
        'dna_extraction': 13, 'sequencing': 14, 'report_ready': 15, 'accepted': 16
      };
      return map[s] !== undefined ? map[s] : -1;
    },
    canUpload() {
      // FASTQ files are uploaded only while the kit is in the "Sequencing In
      // Progress" lab stage. A successful upload advances the lab stage to
      // "Completed Kit" and the business status to Lab Received.
      return this.kitLabStage === "sequencing";
    },
    allFilesCompleted() {
      return this.files.length > 0 && this.files.every(f => f.status !== 0);
    },
    kitStatusClass() {
      if (this.kitStatusInt === 2) return 'lab-awaiting';
      if (this.kitStatusInt === 3) return 'lab-received';
      if (this.kitStatusInt === 16) return 'accepted';
      if (this.kitStatusInt === 13 || this.kitStatusInt === 14) return 'processing';
      if (this.kitStatusInt === 15) return 'report-ready';
      if (this.kitStatusInt === 5) return 'completed';
      return 'no-reports';
    },
  },
  methods: {
    ...mapActions([
      "uploadMtcLabFiles",
      "getMtcLabFiles",
      "sendSearchReqLabV2",
      "fetchWorkQueue",
      "updateMtcLabKitStatus",
    ]),
    switchToQueue() {
      this.activeTab = "queue";
      this.loadWorkQueue();
    },
    async loadWorkQueue() {
      this.queueError = "";
      try {
        await this.fetchWorkQueue();
      } catch (err) {
        this.queueError = "Could not load the work queue. Please try again.";
      }
    },
    // Resolve the next lab action for a queue card from its lab-portal bucket.
    // type 'advance' posts a lab action (accept / start_dna_extraction /
    // start_sequencing); type 'upload'
    // opens the Kit Search tab on this kit so its FASTQ files can be uploaded.
    nextActionForKit(qk) {
      switch (qk._bucket) {
        // New Kits → Accept (business status stays Submitted; lab_stage = accepted).
        case "new": return { type: "advance", action: "accept", label: "Accept" };
        // Accepted Kits → Next Step (DNA Extraction In Progress, status 13).
        case "accepted": return { type: "advance", action: "start_dna_extraction", label: "Next Step" };
        // DNA Extraction → Next Step (Sequencing In Progress, status 14).
        case "dna_extraction": return { type: "advance", action: "start_sequencing", label: "Next Step" };
        // Sequencing → Upload FASTQ Files (advances to Lab Received on success).
        case "sequencing": return { type: "upload", label: "Upload FASTQ Files" };
        // Completed Kits: no lab action (report ready/completed are set elsewhere).
        default: return null;
      }
    },
    // Open the Kit Search tab on a specific kit and jump to its upload form.
    async openUploadForKit(qk) {
      if (!qk || !qk.kit_id) return;
      this.activeTab = "search";
      this.errorMessage = "";
      this.kit = null;
      this.files = [];
      this.resetUploadForm();
      try {
        const res = await this.$store.dispatch("searchMtcLabKit", qk.kit_id);
        if (res) {
          this.kit = res;
          this.files = res.mtc_lab_files || [];
          this.$nextTick(() => {
            const el =
              document.querySelector(".upload-section") ||
              document.querySelector(".kit-section");
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        }
      } catch (err) {
        this.errorMessage = "Could not open the kit for upload. Please search by Kit ID.";
      }
    },
    // Build a per-kit search haystack + filter flags once (memoized via the
    // indexedKits computed) so filtering never re-parses kit data per keystroke.
    indexKit(kit, bucketKey) {
      const parts = [];
      const push = v => { if (v) parts.push(String(v)); };
      push(kit.kit_id);
      push(this.convertKitId(kit.kit_id));
      if (kit.user) {
        push(kit.user.first_name);
        push(kit.user.last_name);
        push(kit.user.email);
      }
      push(kit.collected_first_name);
      push(kit.collected_last_name);
      push(kit.collected_email);
      const samples = Array.isArray(kit.samples) ? kit.samples : [];
      samples.forEach(s => {
        push(s.id);
        push(s.label);
        push(s.type);
        push(s.location);
      });
      push(kit.outdoor_sample);
      const dateRaw = kit.received_at || kit.submission_at || kit.created_at || null;
      const dateObj = dateRaw ? new Date(dateRaw) : null;
      return {
        ...kit,
        _bucket: bucketKey,
        _haystack: parts.join(" ").toLowerCase(),
        _date: dateObj && !isNaN(dateObj.getTime()) ? dateObj : null,
        _hasIndoor: samples.length > 0,
        _hasOutdoor: !!kit.outdoor_sample
      };
    },
    clearFilters() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchInput = "";
      this.searchTerm = "";
      this.statusFilter = "all";
      this.sampleTypeFilter = "all";
      this.dateFrom = "";
      this.dateTo = "";
      this.highlightedKitId = null;
    },
    // After a debounced search, if the term resolves to a Kit ID match, scroll to
    // that card and briefly highlight it.
    maybeHighlightMatch() {
      const norm = this.searchTerm.replace(/[-\s]/g, "").toLowerCase();
      if (!norm) {
        this.highlightedKitId = null;
        return;
      }
      const match = this.indexedKits.find(
        k => String(k.kit_id || "").replace(/[-\s]/g, "").toLowerCase().indexOf(norm) !== -1
      );
      if (!match) {
        this.highlightedKitId = null;
        return;
      }
      this.highlightedKitId = match.kit_id;
      this.$nextTick(() => {
        const ref = this.$refs["card-" + match.kit_id];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      if (this.highlightTimer) clearTimeout(this.highlightTimer);
      this.highlightTimer = setTimeout(() => {
        this.highlightedKitId = null;
      }, 3000);
    },
    async advanceStatus(kit, action) {
      if (!kit || !kit.kit_id || !action || this.advancingKitId) return;
      this.advancingKitId = kit.kit_id;
      try {
        const res = await this.updateMtcLabKitStatus({ kit_id: kit.kit_id, action });
        // Keep the searched-kit panel in sync (status + lab_stage) without a re-search.
        if (this.kit && this.kit.kit_id === kit.kit_id && res) {
          this.kit = { ...this.kit, status: res.status, lab_stage: res.lab_stage };
        }
        // Refresh the queue so the card moves to its new bucket.
        if (this.activeTab === "queue") {
          await this.loadWorkQueue();
        }
      } catch (err) {
        this.queueError =
          (err && err.response && err.response.data && err.response.data.message) ||
          "Could not update the kit status. Please try again.";
      } finally {
        this.advancingKitId = null;
      }
    },
    async searchKit() {
      if (this.errors.any() || !this.isComplete) return;
      this.errorMessage = "";
      this.kit = null;
      this.files = [];
      this.resetUploadForm();

      try {
        this.searchKey = this.parseKitId(this.kit_id);
        const res = await this.$store.dispatch("searchMtcLabKit", this.searchKey);
        if (res) {
          this.kit = res;
          this.files = res.mtc_lab_files || [];
        }
        this.kit_id = null;
        this.$nextTick(() => this.errors.clear());
      } catch (err) {
        this.errorMessage = "Kit not found. Please check the Kit ID and try again.";
      }
    },
    async loadFiles() {
      if (!this.kit) return;
      try {
        const files = await this.getMtcLabFiles(this.kit.id);
        this.files = files || [];
      } catch (err) {
        this.files = [];
      }
    },
    resetUploadForm() {
      this.indoorFastqStaged = {};
      this.outdoorFastqStaged = {};
      this.indoorFastqTypeErrors = {};
      this.outdoorFastqTypeErrors = {};
      this.outdoorSkipped = false;
      this.uploadSuccess = false;
      this.uploadErrorMsg = "";
      this.uploadStatusText = "";
    },
    toggleSkipOutdoor() {
      this.outdoorSkipped = !this.outdoorSkipped;
      if (this.outdoorSkipped) {
        this.outdoorFastqStaged = {};
        this.outdoorFastqTypeErrors = {};
      }
    },
    buildSampleGroups(kind, samples) {
      const prefix = kind === "indoor" ? "fastq_indoor_" : "fastq_outdoor_";
      const relevant = this.files.filter(
        f =>
          (f.file_type === prefix + "r1" || f.file_type === prefix + "r2") &&
          f.sample_index !== null &&
          f.sample_index !== undefined
      );
      const byIndex = new Map();
      relevant.forEach(f => {
        const idx = Number(f.sample_index);
        if (!byIndex.has(idx)) byIndex.set(idx, []);
        byIndex.get(idx).push(f);
      });
      // Order each sample's files: R1 first, then R2.
      const readOrder = { r1: 0, r2: 1 };
      const groups = [];
      Array.from(byIndex.keys())
        .sort((a, b) => a - b)
        .forEach(idx => {
          const sample = samples[idx];
          const filesForSample = byIndex.get(idx).slice().sort((a, b) => {
            const ra = (a.file_type || "").endsWith("_r1") ? 0 : 1;
            const rb = (b.file_type || "").endsWith("_r1") ? 0 : 1;
            return ra - rb;
          });
          groups.push({
            index: idx,
            name: this.sampleDisplayName(sample, idx, kind),
            files: filesForSample
          });
        });
      return groups;
    },
    uploaderName(file) {
      const u = file && file.uploader;
      if (!u) return "";
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return name || u.email || "";
    },
    readLabel(file) {
      if (!file || !file.file_type) return "";
      if (file.file_type.endsWith("_r1")) return "R1";
      if (file.file_type.endsWith("_r2")) return "R2";
      return "";
    },
    sampleDisplayName(sample, index, kind) {
      if (kind === "indoor") {
        if (sample && sample.type) return sample.type;
        if (sample && sample.label) return sample.label;
        return `Indoor Sample #${index + 1}`;
      }
      if (sample && sample.value) return sample.value;
      return "Outdoor Sample";
    },
    sampleSlug(sample, index, kind) {
      const raw = this.sampleDisplayName(sample, index, kind);
      const cleaned = String(raw)
        .replace(/\s+/g, "_")
        .replace(/[^A-Za-z0-9_]/g, "");
      if (cleaned) return cleaned;
      return kind === "indoor"
        ? `IndoorSample${index + 1}`
        : `OutdoorSample${index + 1}`;
    },
    fastqExtension(name) {
      // Preserve the file's real encoding. Renaming a plain .fastq to .fastq.gz
      // used to store an uncompressed file under a name claiming it was
      // gzipped, which breaks anything downstream that trusts the extension.
      return /\.fastq\.gz$/i.test(name || "") ? ".fastq.gz" : ".fastq";
    },
    isFastqGzFilename(name) {
      return /\.fastq(\.gz)?$/i.test(name || "");
    },
    isSlotMissing(kind, index, read) {
      if (kind === "indoor") {
        return !this.getStagedFastq("indoor", index, read);
      }
      if (this.outdoorSkipped) return false;
      if (this.getStagedFastq("outdoor", index, read)) return false;

      // Outdoor sample is optional. Only mark missing if partial files (e.g. R1 but no R2) are staged.
      const hasR1 = !!this.getStagedFastq("outdoor", index, "R1");
      const hasR2 = !!this.getStagedFastq("outdoor", index, "R2");
      return hasR1 || hasR2;
    },
    getSlotTypeError(kind, index, read) {
      const bucket =
        kind === "indoor" ? this.indoorFastqTypeErrors : this.outdoorFastqTypeErrors;
      const slots = bucket[index] || {};
      return slots[read] || "";
    },
    setSlotTypeError(kind, index, read, message) {
      const bucket =
        kind === "indoor" ? "indoorFastqTypeErrors" : "outdoorFastqTypeErrors";
      if (!this[bucket][index]) this.$set(this[bucket], index, {});
      if (message) {
        this.$set(this[bucket][index], read, message);
      } else {
        this.$delete(this[bucket][index], read);
      }
    },
    renameToSampleFile(file, kind, index, read) {
      const samples = kind === "indoor" ? this.indoorSamples : this.outdoorSamples;
      const sample = samples[index];
      const slug = this.sampleSlug(sample, index, kind);
      const ext = this.fastqExtension(file.name);
      const newName = `${slug}_${read}${ext}`;
      try {
        return new File([file], newName, {
          type: file.type || "application/octet-stream",
          lastModified: file.lastModified
        });
      } catch (e) {
        const blob = file.slice(0, file.size, file.type || "application/octet-stream");
        blob.name = newName;
        return blob;
      }
    },
    onSampleFastqSelected(e, kind, index, read) {
      const file = e.target.files[0];
      if (!file) return;
      this.uploadSuccess = false;
      const refName =
        (kind === "indoor" ? "fastqInInput_" : "fastqOutInput_") + index + "_" + read;
      if (!this.isFastqGzFilename(file.name)) {
        this.setSlotTypeError(kind, index, read, "Only .fastq or .fastq.gz files are allowed");
        const ref = this.$refs[refName];
        if (ref) {
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el) el.value = "";
        }
        return;
      }
      this.setSlotTypeError(kind, index, read, "");
      if (kind === "outdoor") {
        this.outdoorSkipped = false;
      }
      const renamed = this.renameToSampleFile(file, kind, index, read);
      const bucket = kind === "indoor" ? "indoorFastqStaged" : "outdoorFastqStaged";
      if (!this[bucket][index]) this.$set(this[bucket], index, {});
      this.$set(this[bucket][index], read, renamed);
    },
    removeSampleFastq(kind, index, read) {
      const bucket = kind === "indoor" ? "indoorFastqStaged" : "outdoorFastqStaged";
      const slots = this[bucket][index];
      if (slots) {
        this.$delete(slots, read);
        if (!slots.R1 && !slots.R2) this.$delete(this[bucket], index);
      }
      this.setSlotTypeError(kind, index, read, "");
      const refName =
        (kind === "indoor" ? "fastqInInput_" : "fastqOutInput_") + index + "_" + read;
      const ref = this.$refs[refName];
      if (ref) {
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) el.value = "";
      }
    },
    getStagedFastq(kind, index, read) {
      const bucket = kind === "indoor" ? this.indoorFastqStaged : this.outdoorFastqStaged;
      const slots = bucket[index] || {};
      return slots[read] || null;
    },
    async uploadFiles() {
      if (!this.hasFilesToUpload || !this.kit) return;
      if (!this.canSubmitUpload) return;

      const allFiles = [];
      const meta = [];

      const collect = (bucket, kind) => {
        Object.keys(bucket).forEach(idx => {
          const slots = bucket[idx] || {};
          ["R1", "R2"].forEach(read => {
            if (slots[read]) {
              allFiles.push(slots[read]);
              meta.push({
                file_type:
                  (kind === "indoor" ? "fastq_indoor_" : "fastq_outdoor_") + read.toLowerCase(),
                sample_index: Number(idx)
              });
            }
          });
        });
      };
      collect(this.indoorFastqStaged, "indoor");
      collect(this.outdoorFastqStaged, "outdoor");

      if (allFiles.length === 0) return;

      this.uploading = true;
      this.uploadSuccess = false;
      this.uploadErrorMsg = "";
      this.uploadProgress = 0;
      this.uploadStatusText = "";

      try {
        const result = await this.uploadMtcLabFiles({
          kit_id: this.kit.kit_id,
          files: allFiles,
          file_meta: meta,
          onProgress: (p) => { this.uploadProgress = p; },
          // Files go up one request at a time, so name the one in flight.
          onFileProgress: ({ index, total, name }) => {
            this.uploadStatusText = `Uploading ${index + 1} of ${total} — ${name}`;
          }
        });
        // resetUploadForm() clears uploadSuccess, so set the flag/message after.
        this.resetUploadForm();
        this.files = (result && result.files) || [];
        // Business status is unchanged by upload; only the internal lab_stage
        // advances to 'completed'. Keep both in sync on the searched-kit panel.
        if (result && (result.kit_status || result.lab_stage)) {
          this.kit = {
            ...this.kit,
            status: result.kit_status || this.kit.status,
            lab_stage: result.lab_stage || this.kit.lab_stage
          };
        }
        this.uploadSuccess = true;
        this.uploadSuccessMsg =
          result && result.status_changed
            ? "FASTQ files uploaded — kit moved to Completed Kits."
            : "Files uploaded successfully!";
        // Keep the Lab Work Queue columns/counts in sync without a page reload.
        this.fetchWorkQueue().catch(() => {});
      } catch (err) {
        // Surface backend per-slot missing-file errors inline; no global toast.
        const data = err && err.response && err.response.data;
        const missing = (data && data.data && data.data.missing) || [];
        if (Array.isArray(missing) && missing.length > 0) {
          missing.forEach(label => {
            const m = /^Indoor Sample #(\d+) (R1|R2)$/.exec(label);
            if (m) {
              this.setSlotTypeError("indoor", Number(m[1]) - 1, m[2], `${m[2]} FASTQ file is required`);
              return;
            }
            const o = /^Outdoor Sample (R1|R2)$/.exec(label);
            if (o) {
              this.setSlotTypeError("outdoor", 0, o[1], `${o[1]} FASTQ file is required`);
            }
          });
          this.uploadErrorMsg = (data && data.message) ||
            "Some required FASTQ files are still missing.";
        } else if (err && err.response) {
          // A real HTTP error from the API — show what it said.
          this.uploadErrorMsg = (data && data.message) ||
            `Upload failed (${err.response.status}). Please try again.`;
        } else if (err && err.message) {
          // Thrown by the direct-to-Spaces part uploader rather than by the
          // API — surface it verbatim, since it names the actual cause (a
          // failed part, or the bucket not exposing ETag).
          this.uploadErrorMsg = err.message;
        } else {
          this.uploadErrorMsg =
            "The upload was interrupted before it finished. Files that already " +
            "completed have been saved — re-select the remaining ones and upload again.";
        }
        // Reflect anything that did land so the user can see what's left to do.
        this.loadFiles();
      } finally {
        this.uploading = false;
        this.uploadStatusText = "";
      }
    },
    getStatusLabel(status) {
      const labels = {
        0: "Sold",
        1: "Activated",
        2: "Submitted",
        3: "Lab Received",
        4: "On Hold",
        5: "Analysis Completed",
        6: "Problematic",
        7: "Delivered",
        8: "Archived",
        9: "Returned",
        10: "Analysis Failed",
        11: "Analysis Partially Failed",
        12: "Inactive",
        13: "DNA Extraction In Progress",
        14: "Sequencing In Progress",
        15: "Report Ready",
        16: "Accepted",
      };
      return labels[status] || "Unknown";
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    },
  },
  watch: {
    // Debounce the search input (300ms) so filtering/highlighting runs once the
    // user pauses typing rather than on every keystroke.
    searchInput(val) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchTerm = val;
        this.maybeHighlightMatch();
      }, 300);
    },
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.highlightTimer) clearTimeout(this.highlightTimer);
  },
};
</script>

<style scoped lang="scss">
.dashboard {
  padding: 140px 0 10px 0;
  background-color: #f4f8f9;
  max-width: 100%;
  width: 100vw;
  min-height: 95vh;
  position: relative;
}
.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
}
.section-title {
  font-family: TTNormsBold, sans-serif;
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}
.search-section {
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}
.search-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.input-wrap {
  width: 100%;
  max-width: 334px;
  position: relative;
}
.search-input {
  width: 100%;
  padding: 12px 30px;
  border: 1px solid #c5c5c5;
  border-radius: 10px;
  font-size: 16px;
  font-family: TTNormsMedium, sans-serif;
  outline: none;
  text-align: center;
  text-transform: uppercase;
  box-sizing: border-box;
  margin-bottom: 6px;
}
.validate-error {
  display: block;
  color: #e53935;
  font-size: 13px;
  margin-bottom: 6px;
  text-align: center;
}
.search-input:focus {
  border-color: rgb(77, 144, 254);
}
.search-btn {
  width: 100%;
  max-width: 334px;
  height: 45px;
  padding: 0 30px;
  background-color: #4caf50;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-family: TTNormsMedium, sans-serif;
}
.search-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.error-msg {
  color: #e53935;
  font-size: 14px;
  margin-top: 10px;
  display: block;
}
.status-warning {
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 15px;
  padding: 20px 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
.warning-msg {
  color: #e65100;
  font-size: 16px;
  font-family: TTNormsMedium, sans-serif;
  margin: 0;
}
.success-msg {
  color: #4caf50;
  font-size: 14px;
  margin-top: 10px;
  display: block;
}
.upload-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.upload-progress-bar {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #e2e8f0;
  overflow: hidden;
}
.upload-progress-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #43b02a, #1976d2);
  transition: width 0.2s ease;
}
.upload-progress-text {
  font-family: TTNormsMedium, sans-serif;
  font-size: 13px;
  color: #555;
  min-width: 38px;
  text-align: right;
}
.upload-progress-file {
  font-size: 12px;
  color: #777;
  margin-left: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}
.queue-card-hint {
  margin-top: 8px;
  font-size: 11px;
  color: #8a94a6;
  font-family: TTNormsMedium, sans-serif;
  font-style: italic;
}
.kit-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.kit-info,
.upload-section,
.files-section {
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
h3 {
  font-family: TTNormsBold, sans-serif;
  font-size: 18px;
  color: #333;
  margin: 0 0 15px 0;
}
.kit-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.kit-timeline-wrap {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef2f5;
}
.detail-row {
  display: flex;
  gap: 10px;
}
.label {
  font-family: TTNormsBold, sans-serif;
  color: #666;
  min-width: 130px;
}
.value {
  font-family: TTNormsMedium, sans-serif;
  color: #333;
}
.status-badge {
  background: #e3f2fd;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 13px;
}
.no-reports {
  background: #f5f5f5;
  color: #757575;
}
.processing {
  background: #fff3e0;
  color: #e65100;
}
.completed {
  background: #e8f5e9;
  color: #2e7d32;
}
.lab-awaiting {
  background: #fff8e1;
  color: #f57f17;
}
.lab-received {
  background: #e1f5fe;
  color: #0277bd;
}
.accepted {
  background: #e0f4fe;
  color: #0369a1;
}
.report-ready {
  background: #fff7e6;
  color: #b45309;
}
/* ── Tabs ── */
.tab-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.tab-btn {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d7dde2;
  background: #fff;
  border-radius: 10px;
  font-family: TTNormsMedium, sans-serif;
  font-size: 15px;
  color: #555;
  cursor: pointer;
}
.tab-btn.active {
  background: #005eb8;
  border-color: #005eb8;
  color: #fff;
}
/* ── Work Queue ── */
.work-queue {
  background: #fff;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
/* ── Work-queue filters (sticky) ── */
.queue-filters {
  position: sticky;
  top: 100px; /* sits just below the fixed 100px site header */
  z-index: 5;
  background: #fff;
  padding: 14px 16px;
  margin: -20px -20px 16px -20px; /* span the .work-queue padding */
  border-radius: 15px 15px 0 0;
  border-bottom: 1px solid #eceff1;
  box-shadow: 0 4px 10px -6px rgba(0, 0, 0, 0.15);
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}
.filter-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 150px;
  min-width: 130px;
}
.filter-field.filter-search {
  flex: 2 1 260px;
}
.filter-field.filter-actions {
  flex: 0 0 auto;
  justify-content: flex-end;
}
.filter-label {
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.filter-input {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid #d7dde2;
  border-radius: 8px;
  font-size: 14px;
  font-family: TTNormsMedium, sans-serif;
  color: #333;
  outline: none;
  background: #fff;
}
.filter-input:focus {
  border-color: #005eb8;
}
.clear-filters-btn {
  padding: 9px 16px;
  background: #fff;
  border: 1px solid #d7dde2;
  border-radius: 8px;
  font-family: TTNormsMedium, sans-serif;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
}
.clear-filters-btn:hover:not(:disabled) {
  background: #f4f8f9;
  border-color: #b9c2c9;
}
.clear-filters-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.filter-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 10px;
}
.results-count {
  font-family: TTNormsBold, sans-serif;
  font-size: 13px;
  color: #333;
}
.filter-hint {
  font-size: 12px;
  color: #999;
}
.queue-no-results {
  text-align: center;
  color: #888;
  font-family: TTNormsMedium, sans-serif;
  font-size: 15px;
  padding: 40px 0;
}
.queue-card {
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.queue-card.highlighted {
  border-color: #43b02a;
  box-shadow: 0 0 0 2px rgba(67, 176, 42, 0.5);
  animation: queue-card-pulse 1.2s ease-in-out 0s 2;
}
@keyframes queue-card-pulse {
  0% { box-shadow: 0 0 0 2px rgba(67, 176, 42, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(67, 176, 42, 0.22); }
  100% { box-shadow: 0 0 0 2px rgba(67, 176, 42, 0.5); }
}
@media (max-width: 768px) {
  .queue-filters {
    top: 70px; /* mobile header is 70px tall */
  }
  .filter-field,
  .filter-field.filter-search {
    flex: 1 1 100%;
  }
  .filter-field.filter-actions .clear-filters-btn {
    width: 100%;
  }
}
.queue-columns {
  display: flex;
  gap: 12px;
  overflow-x: auto;
}
.queue-col {
  flex: 1 1 0;
  min-width: 170px;
  background: #f7f9fb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}
.queue-col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-top: 3px solid #ccc;
  border-radius: 10px 10px 0 0;
}
.queue-col-title {
  font-family: TTNormsBold, sans-serif;
  font-size: 13px;
  color: #333;
}
.queue-col-count {
  background: #fff;
  border-radius: 12px;
  padding: 1px 9px;
  font-size: 12px;
  font-family: TTNormsBold, sans-serif;
  color: #555;
}
.queue-col-body {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.queue-card {
  background: #fff;
  border: 1px solid #eceff1;
  border-radius: 8px;
  padding: 10px;
}
.queue-card-id {
  font-family: TTNormsBold, sans-serif;
  font-size: 13px;
  color: #1976d2;
}
.queue-card-status {
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  margin-top: 2px;
}
.queue-card-meta {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}
.queue-card-due {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}
.queue-advance-btn {
  margin-top: 8px;
  width: 100%;
  padding: 6px 8px;
  background: #43b02a;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  cursor: pointer;
}
.queue-advance-btn.inline {
  width: auto;
  margin-top: 0;
  padding: 4px 12px;
}
.queue-advance-btn.upload {
  background: #1976d2;
}
.queue-advance-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.queue-card-report {
  margin-top: 6px;
  font-family: TTNormsBold, sans-serif;
  font-size: 11px;
  color: #2e7d32;
}
.queue-card-files {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #eceff1;
}
.queue-files-label {
  font-family: TTNormsBold, sans-serif;
  font-size: 11px;
  color: #7b1fa2;
  margin-bottom: 3px;
}
.queue-files-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.queue-files-list li {
  font-family: TTNormsMedium, sans-serif;
  font-size: 11px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
}
.queue-empty {
  text-align: center;
  color: #aaa;
  font-size: 12px;
  padding: 10px 0;
  font-family: TTNormsMedium, sans-serif;
}
.queue-error {
  display: block;
  color: #e53935;
  font-size: 13px;
  margin-top: 10px;
}
.upload-area {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.upload-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #eee;
}
.upload-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.skip-outdoor-btn {
  background: #fff;
  border: 1px solid #005eb8;
  color: #005eb8;
  border-radius: 6px;
  padding: 5px 12px;
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  cursor: pointer;
  margin-left: auto;
  transition: all 0.2s ease;
}
.skip-outdoor-btn:hover {
  background: #e0f4fe;
}
.skip-outdoor-btn.is-skipped {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #475569;
}
.outdoor-skipped-banner {
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-family: TTNormsMedium, sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
}
.upload-group-title {
  font-family: TTNormsBold, sans-serif;
  font-size: 14px;
  color: #333;
}
.upload-group-hint {
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  color: #999;
}
.file-input {
  display: none;
}
.file-label {
  display: inline-block;
  padding: 12px 20px;
  background: #fff;
  border: 2px dashed #ccc;
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  font-family: TTNormsMedium, sans-serif;
  color: #666;
}
.file-label:hover {
  border-color: #999;
}
.fastq-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sample-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #fff;
}
.sample-card-header {
  font-family: TTNormsBold, sans-serif;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
}
.sample-slug {
  font-family: TTNormsMedium, sans-serif;
  font-size: 12px;
  color: #999;
  margin-left: 6px;
}
.fastq-row-sample {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.required-marker {
  color: #d9534f;
  margin-left: 2px;
}
.fastq-label.slot-missing {
  border-color: #d9534f;
  color: #d9534f;
}
.slot-error {
  color: #d9534f;
  font-size: 12px;
  font-family: TTNormsMedium, sans-serif;
  margin-top: 4px;
}
.selected-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.file-tag {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: TTNormsMedium, sans-serif;
}
.fastq-tag {
  background: #f3e5f5;
}
.remove-file {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #999;
  padding: 0;
  line-height: 1;
}
.add-more-btn {
  background: none;
  border: 1px dashed #1976d2;
  border-radius: 8px;
  color: #1976d2;
  padding: 8px 16px;
  cursor: pointer;
  font-family: TTNormsMedium, sans-serif;
  font-size: 13px;
  align-self: flex-start;
}
.add-more-btn:hover {
  background: #e3f2fd;
}
.validation-error {
  color: #e53935;
  font-size: 13px;
  font-family: TTNormsMedium, sans-serif;
}
.upload-btn {
  padding: 12px 30px;
  background-color: #1976d2;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  font-family: TTNormsMedium, sans-serif;
  align-self: flex-start;
}
.upload-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.file-type-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-family: TTNormsBold, sans-serif;
  text-transform: uppercase;
}
.type-fastq {
  background: #f3e5f5;
  color: #7b1fa2;
}
.no-data {
  color: #999;
  font-family: TTNormsMedium, sans-serif;
  padding: 20px 0;
}
.grouped-files {
  margin-bottom: 20px;
}
.group-title {
  font-family: TTNormsBold, sans-serif;
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}
.sample-file-group {
  margin-bottom: 12px;
}
.sample-file-name {
  font-family: TTNormsBold, sans-serif;
  font-size: 14px;
  color: #1976d2;
  margin: 6px 0 4px 0;
}
.sample-file-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0 14px;
}
.sample-file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-family: TTNormsMedium, sans-serif;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #f4f4f4;
}
.sample-file-row:last-child {
  border-bottom: none;
}
.file-name-cell {
  flex: 1 1 auto;
  word-break: break-all;
}
.file-uploader-cell {
  color: #777;
  font-size: 12px;
  font-style: italic;
}
.file-date-cell {
  color: #999;
  font-size: 12px;
  margin-left: auto;
}
.files-table {
  width: 100%;
  border-collapse: collapse;
  font-family: TTNormsMedium, sans-serif;
}
.files-table th {
  text-align: left;
  padding: 10px;
  border-bottom: 2px solid #eee;
  font-family: TTNormsBold, sans-serif;
  color: #666;
}
.files-table td {
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}
@media (max-width: 768px) {
  .dashboard {
    padding: 100px 0 10px 0;
  }
  .search-wrap {
    flex-direction: column;
  }
  .kit-info,
  .upload-section,
  .files-section {
    padding: 20px;
  }
}
</style>
